import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Menu, X, LayoutDashboard } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const NAV_LINKS = [
  { label: 'Industries', to: '/industries' },
  { label: 'Locations', to: '/locations' },
  { label: 'Pricing', to: '/#pricing', isAnchor: true },
  { label: 'Use Cases', to: '/use-cases' },
  { label: 'Refer & Earn', to: '/refer-and-earn' },
  { label: 'Blog', to: '/blog' },
  { label: 'FAQ', to: '/faq' },
];

export function PublicHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, isAuthenticated } = useAuth();

  const isActive = (to) => location.pathname === to;

  const handleNavClick = (link, e) => {
    if (link.isAnchor) {
      e.preventDefault();
      if (location.pathname === '/') {
        document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/');
        setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 400);
      }
    }
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100" data-testid="public-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="header-logo">
          <img src="/harmonix-logo.png" alt="Harmonix" className="h-10 w-auto" />
        </Link>
        <nav className="hidden lg:flex items-center gap-7 text-sm">
          {NAV_LINKS.map(link => (
            <Link
              key={link.label}
              to={link.to}
              onClick={(e) => link.isAnchor && handleNavClick(link, e)}
              className={`font-medium transition-colors ${isActive(link.to) ? 'text-teal-700' : 'text-slate-500 hover:text-teal-700'}`}
            >
              {link.label}
            </Link>
          ))}
        </nav>
        <div className="hidden sm:flex items-center gap-3">
          {isAuthenticated ? (
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-teal-600 hover:bg-teal-700 text-white font-semibold shadow-sm"
              data-testid="header-dashboard-btn"
            >
              <LayoutDashboard className="w-4 h-4 mr-1.5" />
              Go to Dashboard
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate('/login')} className="text-slate-600 font-medium" data-testid="header-login-btn">
                Log In
              </Button>
              <Button variant="outline" onClick={() => navigate('/contact?type=demo')} className="border-teal-200 text-teal-700 hover:bg-teal-50 font-medium" data-testid="header-book-demo-btn">
                Book a Demo
              </Button>
              <Button onClick={() => navigate('/login?signup=true')} className="bg-teal-600 hover:bg-teal-700 text-white font-semibold shadow-sm" data-testid="header-signup-btn">
                Start Free
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </>
          )}
        </div>
        <button className="lg:hidden p-2 text-slate-600" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 px-4 py-4 space-y-1 shadow-lg">
          {NAV_LINKS.map(link => (
            <Link key={link.label} to={link.to} onClick={(e) => handleNavClick(link, e)} className="block text-sm text-slate-600 py-2.5 px-3 rounded-lg hover:bg-slate-50">
              {link.label}
            </Link>
          ))}
          <div className="flex gap-2 pt-3 border-t border-slate-100 mt-2">
            {isAuthenticated ? (
              <Button
                size="sm"
                onClick={() => { navigate('/dashboard'); setMobileOpen(false); }}
                className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="mobile-header-dashboard-btn"
              >
                <LayoutDashboard className="w-4 h-4 mr-1.5" />
                Dashboard
              </Button>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => { navigate('/login'); setMobileOpen(false); }} className="flex-1">Log In</Button>
                <Button variant="outline" size="sm" onClick={() => { navigate('/contact?type=demo'); setMobileOpen(false); }} className="flex-1 border-teal-200 text-teal-700" data-testid="mobile-header-book-demo-btn">Book a Demo</Button>
                <Button size="sm" onClick={() => { navigate('/login?signup=true'); setMobileOpen(false); }} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white">Start Free</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
